import { Company, Id, Observer, UserRole } from '@nextforce/api'
import { Button, Text, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import { useManagementTeamActions } from 'management_team/management_actions_context'
import { compareUsersByName, filterUsers } from 'management_team/util'
import { useMemo, useState } from 'react'
import { ActivityIndicator, View } from 'react-native'
import { FlatList } from 'react-native-gesture-handler'
import { SafeAreaView } from 'react-native-safe-area-context'
import { ListEmptyComponent } from 'shared/components/empty_list'
import { LastUpdatedPill } from 'shared/components/last_updated_pill'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import RefreshControl from 'shared/components/refresh_control'
import { UserAvatar } from 'shared/components/user/user_avatar'
import { CompanyInfoView } from './company_info'
import { MemberSearchBar } from './member_search_bar'

interface Props {
  company: Company | undefined
  role: UserRole | undefined
  users: Observer[] | undefined
  tempUsers: Observer[]
  lastUpdatedAt?: number
  isRefreshing?: boolean
  onPressUser: (userId: Id) => void
  onAddUser: () => void
  onRefresh: () => Promise<void>
  onPressRecentChanges: () => void
}

export function ManagementTeamView({
  users,
  tempUsers,
  role,
  onPressUser,
  lastUpdatedAt,
  company,
  isRefreshing,
  onAddUser,
  onRefresh,
  onPressRecentChanges,
}: Props) {
  const styles = useStyles()
  const [searchTerm, setSearchTerm] = useState('')
  const [searchEnabled, setSearchEnabled] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const filteredUsers = useMemo(() => {
    if (searchEnabled) {
      return filterUsers(searchTerm, users)
    }
    const filteredTempUsers = tempUsers.filter(user => user.role === role)
    if (users) {
      return [...filteredTempUsers, ...users].sort(compareUsersByName)
    }
    return filteredTempUsers.sort(compareUsersByName)
  }, [searchEnabled, tempUsers, users, searchTerm, role])

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <FlatList
          keyboardShouldPersistTaps={'handled'}
          style={{ flex: 1 }}
          data={filteredUsers}
          renderItem={({ item }) => (
            <UserItem user={item} onPressUser={onPressUser} />
          )}
          refreshControl={
            searchEnabled ? undefined : (
              <RefreshControl
                enabled
                refreshing={refreshing}
                onRefresh={async () => {
                  setRefreshing(true)
                  await onRefresh()
                  setRefreshing(false)
                }}
              />
            )
          }
          ListEmptyComponent={
            searchEnabled ? (
              <ListEmptyComponent title={t('noMembersFound')} />
            ) : null
          }
          ListHeaderComponent={
            searchEnabled ? (
              <MemberSearchBar
                searchTerm={searchTerm}
                filteredUsers={filteredUsers}
                setSearchEnabled={setSearchEnabled}
                setSearchTerm={setSearchTerm}
              />
            ) : (
              <View style={styles.listHeaderContainer}>
                <LastUpdatedPill
                  lastUpdatedAt={lastUpdatedAt}
                  isRefreshing={isRefreshing}
                  onPress={onRefresh}
                />
                <CompanyInfoView
                  company={company}
                  memberCount={filteredUsers.length}
                  onAddUser={onAddUser}
                  setSearchEnabled={setSearchEnabled}
                />
                {users?.length || tempUsers.length ? (
                  <View style={styles.card}>
                    <Text style={styles.listHeaderText}>{t('members')}</Text>
                  </View>
                ) : null}
              </View>
            )
          }
          stickyHeaderIndices={[0]}
        />
        {searchEnabled || users?.length || tempUsers.length ? null : (
          <NoMembersComponent />
        )}
        {role === 'observer' ? (
          <View style={styles.floatingView}>
            <Button
              title={t('recentChanges')}
              buttonStyle={styles.helpButton}
              titleStyle={styles.helpButtonText}
              icon={{
                name: 'recent-actors',
                color: styles.helpButtonIcon.color,
                size: styles.helpButtonIcon.height,
                type: 'materialicons',
              }}
              iconPosition="top"
              onPress={onPressRecentChanges}
              titleProps={{ allowFontScaling: false }}
            />
          </View>
        ) : null}
      </View>
    </SafeAreaView>
  )
}

function UserItem({
  user,
  onPressUser,
}: {
  user: Observer
  onPressUser: (userId: Id) => void
  isUpdating?: boolean
}) {
  const styles = useStyles()
  const { getIsUserUpdating } = useManagementTeamActions()
  const isUpdating = getIsUserUpdating(user.id)
  return (
    <PressableOpacity onPress={() => onPressUser(user.id)}>
      <View style={styles.userItemContainer}>
        <UserAvatar user={user} size={'medium'} />
        <View style={styles.textContainer}>
          <View>
            <Text style={styles.name}>
              {user.firstName} {user.lastName}
            </Text>
            {user.jobTitle ? (
              <Text style={styles.text}>{user.jobTitle}</Text>
            ) : null}
            {user.stores ? (
              user.stores.length === 1 ? (
                <View style={styles.storeCountRow}>
                  <View style={styles.storeCountContainer}>
                    <Text style={styles.storeCountText}>{`${t('store')} - ${
                      user.stores[0].storeNumber
                    }`}</Text>
                  </View>
                </View>
              ) : (
                <View style={styles.storeCountRow}>
                  <Text style={styles.text}>{t('stores')}</Text>
                  <View style={styles.storeCountContainer}>
                    <Text
                      style={styles.storeCountText}
                    >{`${user.stores.length}`}</Text>
                  </View>
                </View>
              )
            ) : null}
          </View>
          <ActivityIndicator
            animating={isUpdating}
            color={styles.activity.color}
            style={styles.activity}
          />
        </View>
      </View>
    </PressableOpacity>
  )
}

function NoMembersComponent() {
  const styles = useStyles()
  return (
    <View style={styles.noMembersContainer}>
      <Text style={styles.noMembersText}>{t('noMembers')}</Text>
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.grey6,
  },
  listHeaderContainer: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.grey6,
  },
  userItemContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    backgroundColor: theme.colors.white,
    marginBottom: 2,
  },
  listHeaderText: { fontSize: 16, marginRight: 5 },

  card: {
    backgroundColor: theme.colors.white,
    marginBottom: 2,
    padding: 15,
  },
  textContainer: {
    marginLeft: 10,
    flexDirection: 'row',
    flex: 1,
    justifyContent: 'space-between',
  },
  text: {
    fontSize: 14,
    marginRight: 5,
  },
  name: {
    fontSize: 16,
  },
  storeCountRow: { flexDirection: 'row', alignItems: 'center' },
  storeCountContainer: {
    marginVertical: 5,
    padding: 2,
    backgroundColor: theme.colors.peach,
    borderRadius: 50,
  },
  storeCountText: {
    fontSize: 14,
    paddingHorizontal: 10,
    color: 'black',
  },
  helpButtonText: {
    color: theme.colors?.white,
    fontSize: 16,
  },
  helpButton: {
    width: 100,
    height: 80,
    borderRadius: 10,
    backgroundColor: theme.colors.grey2,
  },
  floatingView: {
    position: 'absolute',
    bottom: 16,
    right: 16,
  },
  helpButtonIcon: {
    color: theme.colors?.white,
    height: 32,
  },
  noMembersContainer: {
    flex: 1,
    alignItems: 'center',
  },
  noMembersText: { fontSize: 20 },
  activity: { marginRight: 20, color: theme.colors.primary },
}))
